import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchHomeImages, uploadUrl } from "@/lib/api";

export default function Home() {
  const { user, isApproved } = useAuth();
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    fetchHomeImages().then(setGallery).catch(() => setGallery([]));
  }, []);

  return (
    <div data-testid="home-page">
      <div className="page-card">
        <section className="px-6 md:px-12 pt-8 pb-16 md:pt-12 md:pb-24">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2" data-testid="home-eyebrow">
            Wholesale order portal
          </p>

          <img
            src="/brand/rowen-logo.png"
            alt="Rowen Kids Club"
            data-testid="home-hero-logo"
            className="h-64 md:h-96 w-auto object-contain select-none block -ml-10 md:-ml-16 -mt-8 md:-mt-12"
            draggable="false"
          />

          <h1
            data-testid="home-hero-title"
            className="-mt-16 md:-mt-24 font-serif text-5xl md:text-7xl tracking-[-0.05em] leading-[1.05] max-w-3xl text-primary"
            style={{ fontFamily: '"Snowyn Snowman", "Caveat Brush", cursive' }}
          >
            Wholesale Order Portal
          </h1>

          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="home-hero-sub">
            Browse collections, shop current in-stock styles, pre-book upcoming seasons, view invoices, download brand content.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            {user ? (
              <Link
                to={isApproved ? "/dashboard" : "/shop"}
                data-testid="hero-cta-shop"
                className="inline-flex items-center gap-2 px-5 py-3 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
              >
                {isApproved ? "Go to dashboard" : "Browse products"}
                <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  data-testid="hero-cta-apply"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Apply for wholesale access
                  <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded border border-border text-foreground text-sm font-medium hover:bg-secondary transition-colors"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>

          {gallery.length > 0 && (
            <div
              data-testid="home-gallery"
              className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
            >
              {gallery.map((img) => {
                const tile = (
                  <div className="relative overflow-hidden rounded-lg bg-secondary aspect-[4/5] group h-full">
                    <img
                      src={uploadUrl(img.url)}
                      alt={img.alt || ""}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                );
                return img.product_id ? (
                  <Link
                    key={img.id}
                    to={`/product/${img.product_id}`}
                    data-testid={`home-gallery-item-${img.id}`}
                    className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
                    aria-label={img.alt || "View product"}
                  >
                    {tile}
                  </Link>
                ) : (
                  <div
                    key={img.id}
                    data-testid={`home-gallery-item-${img.id}`}
                  >
                    {tile}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
